import React, { useCallback, useId } from "react";
import { Box, Button, InputText, Stack, Text, Dialog, useModals } from "@gusarov-studio/rubik-ui";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import type { NewFolder } from "../../../types/Resource";
import { useCreateFolderMutation } from "..";
import { NewFolderSchema } from "../../../schemas/FolderSchema";

interface AddFolderDialogContainerProps {
  parentId?: string | null;
}

const AddFolderDialogContainer: React.FC<AddFolderDialogContainerProps> = ({ parentId = null }) => {
  const [createFolder, { isLoading: isCreating }] = useCreateFolderMutation();

  const modals = useModals();
  const closeDialog = useCallback(() => {
    modals.close();
  }, [modals]);

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<NewFolder>({
    defaultValues: {
      name: "",
      icon: "folder",
      parent_id: parentId,
    },
    mode: "onSubmit",
    reValidateMode: "onChange",
    resolver: zodResolver(NewFolderSchema),
  });

  const rename = useCallback(
    (folder: NewFolder) => {
      createFolder({
        folder,
      }).then(() => {
        closeDialog();
      });
    },
    [createFolder, closeDialog],
  );

  const nameFieldId = useId();
  const formId = useId();

  return (
    <Dialog.Root open={true}>
      <Dialog.Content width="400px">
        <Dialog.Decoration type="grid" />
        <Dialog.Close
          onClick={closeDialog}
          disabled={isCreating}
        />
        <Dialog.Header>
          <Dialog.Title>Add folder</Dialog.Title>

          <Dialog.Description>Please give a name for your folder</Dialog.Description>
        </Dialog.Header>

        <Box padding="20px 24px 32px">
          <form
            id={formId}
            onSubmit={handleSubmit(rename)}
          >
            <Stack spacing={6}>
              <Text
                as="label"
                htmlFor="name"
                weight="500"
                size="14"
                color="primary"
              >
                Folder name
              </Text>

              <InputText
                id={nameFieldId}
                autoFocus
                invalid={Boolean(errors.name)}
                disabled={isCreating}
                {...register("name")}
              />

              {errors.name?.message && (
                <Text
                  color="danger"
                  size="14"
                >
                  {errors.name?.message}
                </Text>
              )}
            </Stack>
          </form>
        </Box>

        <Dialog.Footer>
          <Button
            appearance="outline"
            variant="secondary"
            size="40"
            onClick={closeDialog}
            disabled={isCreating}
          >
            Cancel
          </Button>

          <Button
            variant="accent"
            size="40"
            type="submit"
            disabled={isCreating}
            form={formId}
          >
            Submit
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog.Root>
  );
};

export { AddFolderDialogContainer };
